import { Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import {Contacts, Deductions, Tax, Payee, PayeeDashboard} from "app/payroll/payroll";
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TextboxQuestion } from "app/payroll/payee-management/payee-details/question-textbox";
import { QuestionBase } from "app/payroll/payee-management/payee-details/question-base";
import { DropdownQuestion } from 'app/payroll/payee-management/payee-details/question-dropdown';
import {Observable} from "rxjs";
import {AddressMap, Garnishment, PayeeACH, QueryResult, UserType} from "@app/shared";


@Injectable()
export class PayeeDetailsService {
  selectedStateQuestions;

  constructor (private http: HttpClient) { }
  private payeeMainSummary = `/payroll/payees/summary`;
  private payeeMainSearch = `/payroll/payees/search?`;
  private payeeDetails = `/payroll/payees/`;
  private payeeEarnings = `/payroll/payees/earning-categories/`;
  private payeeDeductions = `/payroll/payees/deduction-categories/`;
  private payeeWorkLocation = '/payroll/work-locations?type=';
  private payeeClientLocation = '/payroll/client-locations/';
  private savePayeeURL = `/payroll/payees`;
  private updatePayeeURL = '/hr/users';
  private deletePayeeurl = `/payroll/payees/`;
  private deletePayeeAchUrl = "/payroll/payees/ach-detail/";


  getPayeeMainSummary(): Observable<PayeeDashboard> {
    const url = `/payroll/payees/summary`;
    return this.http.get<PayeeDashboard>(url);
  }

  searchPayeeMain(searchOption: any): Observable<QueryResult> {
    let query = "";
    if (searchOption.userId) {
      query += "payeeId=" + searchOption.userId;
    }
    if (searchOption.type) {
      if (query != "") {
        query += "&";
      }
      query += "flsaType=" + searchOption.type;
    }
    if (searchOption.active) {
      if (query != "") {
        query += "&";
      }
      if (searchOption.active == "true" || searchOption.active == "false") {
        query += "active=" + searchOption.active;
      }
    }
    let url = "/payroll/payees/search?" + query;
    return this.http.get<QueryResult>(url);
  }

  getPayeeDetailsById(payeeId: number): Observable<any> {
    const url = `/payroll/payees/${payeeId}`;
    return this.http.get<any>(url);
  }

  getPayeeWorkLocations(locationType: string): Observable<AddressMap[]> {
    const url = `/payroll/work-locations?type=${locationType}`;
    return this.http.get<any>(url);
  }

  getPayeeClientLocations(userId: number) {
    const url = `/payroll/client-locations/${userId}`;
    return this.http.get<any>(url);
  }

  saveOrUpdatePayee(data): Observable<any> {
    let url = `/payroll/payees`;
    if (data.id) {
      if (data.subtype == UserType.Payee) {
        return this.http.put<any>(`${url}/${data.id}`, data);
      } else {
        return this.http.put<any>(`${url}/${data.id}`, data);
      }
    } else {
      return this.http.post<Payee>(`${url}`, data);
    }
  }

  getPayeeEarning(payeeId: number): Observable<QueryResult> {
    const url = `/payroll/payees/earning-categories/${payeeId}`;
    return this.http.get<QueryResult>(url);
  }

  getPayeeDeductions(payeeId: number): Observable<any> {
    const url = `/payroll/payees/deduction-categories/${payeeId}`;
    return this.http.get<Deductions>(url);
  }

  getProviderPlans(payeeId: number): Observable<any> {
    let url = `/hr/benefit-provider-plans/cost-summary/${payeeId}`;
    return this.http.get<Deductions>(url);
  }

  getTax() {
    const url = `data/payee/orgtaxwithholding.json`;
    return this.http.get<Tax>(url);
  }

  getContacts() {
    const url = `data/payee/contacts.json`;
    return this.http.get<Contacts>(url);
  }

  toFormGroup(questions: QuestionBase<any>[]) {
    let group: any = {};
    questions.forEach(question => {
      group[question.key] = question.required ? new FormControl(question.value || '', Validators.required)
        : new FormControl(question.value || '');
    });
    return new FormGroup(group);
  }

  getQuestions(data) {
    let questions: QuestionBase<any>[] = [];
    let ft = [];
    for (let i = 0; i < data.length; i++) {
      if (data[i].paramType == 'DropdownQuestion') {
        ft.push(new DropdownQuestion({
          key: data[i].paramLabel,
          label: data[i].paramLabel,
          value: data[i].paramValue,
          options: [
            {key: 'test', value: 'test'}
          ],
          required: true,
          order: i
        }));
      } else {
        ft.push(new TextboxQuestion({
          key: data[i].paramLabel,
          label: data[i].paramLabel,
          value: data[i].paramValue,
          required: true,
          order: i
        }));
      }
    }

    questions = ft;

    return questions.sort((a, b) => a.order - b.order);
  }

  deletePayee(payeeId: number): Observable<any> {
    const url = `/payroll/payees/${payeeId}`;
    return this.http.delete<any>(url);
  }

  deletePayeeAch(payeeAch: PayeeACH): Observable<any> {
    const url = `/payroll/payees/ach-detail/${payeeAch.id}`;
    return this.http.delete<any>(url);
  }

  savePayeeAch(payeeId: number, payeeAch: PayeeACH): Observable<any> {
    let savePayeeAchUrl = `/payroll/payees/${payeeId}/ach-detail`;
    return this.http.post<any>(savePayeeAchUrl, payeeAch);
  }

  updatePayeeAch(payeeId: number, payeeAch: PayeeACH): Observable<any> {
    let updatePayeeAchUrl = `/payroll/payees/${payeeId}/ach-detail/${payeeAch.id}`;
    return this.http.put<any>(updatePayeeAchUrl, payeeAch);
  }

  saveEmployeeGarnishment(payeeId: number, employeeGarnishment: Garnishment): Observable<any> {
    let saveGarnishmentURL = `/payroll/payees/${payeeId}/garnishments`;
    return this.http.post<any>(saveGarnishmentURL, employeeGarnishment);
  }

  updateEmployeeGarnishment(payeeId: number, employeeGarnishment: Garnishment): Observable<any> {
    let updateGarnishmentURL = `/payroll/payees/${payeeId}/garnishments/${employeeGarnishment.id}`;
    return this.http.put<any>(updateGarnishmentURL, employeeGarnishment);
  }

  deleteEmployeeGarnishment(payeeId: number, employeeGarnishment: Garnishment): Observable<any> {
    let updateGarnishmentURL = `/payroll/payees/${payeeId}/garnishments/${employeeGarnishment.id}`;
    return this.http.delete<any>(updateGarnishmentURL);
  }


}

